import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  FormControl,
  Checkbox,
  FormControlLabel,
  IconButton,
  Card,
  CardContent,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  ListItemText,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Radio,
  RadioGroup
} from "@mui/material";
import { FaArrowLeft, FaArrowRight, FaWhatsapp, FaPlus, FaSync } from "react-icons/fa";
import { IoLogoWhatsapp } from "react-icons/io5";
import { FaDownload } from "react-icons/fa";
import { getData, postData, postFileUpload, putData } from "../../../Webservice/Webservice";
import * as XLSX from 'xlsx';
import { API_URL } from "../../../Utils/url";

function Whatsapp() {
  const [activeTab, setActiveTab] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [dynamicFields, setDynamicFields] = useState(['name', 'mobile', 'email', 'city', 'state', 'gender', 'dob']);
  const [variableTypes, setVariableTypes] = useState({});
  
  const [audienceSource, setAudienceSource] = useState('database');
  const [externalFile, setExternalFile] = useState(null);

  const [showLogsDialog, setShowLogsDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignLogs, setCampaignLogs] = useState([]);
  
  // Campaign Form State
  const [formData, setFormData] = useState({
    name: "",
    templateName: "",
    templateLanguage: "en_US",
    variableValues: {},
    headerFile: null,
    scheduledAt: null,
    isScheduled: false,
    date: ""
  });

  // Template Form State
  const [templateFormData, setTemplateFormData] = useState({
    name: "",
    category: "MARKETING",
    templateName: "",
    language: "en",
    headerType: "NONE", // NONE, TEXT, MEDIA
    headerContent: "", 
    headerFile: null,
    headerPreview: "",
    bodyText: "",
    footerText: "",
    buttons: [] 
  });

  const extractVariables = (text) => {
    if (!text) return [];
    const regex = /\{\{(\d+)\}\}/g;
    const matches = [...text.matchAll(regex)];
    return matches.map(m => m[1]).filter((v, i, a) => a.indexOf(v) === i);
  };

  // Customer Filter State
  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState([]);
  const [filterData, setFilterData] = useState({
    promotionType: "whatsapp",
    city: [],
    gender: [],
    status: [],
    customerType: []
  });

  const tokenValue = sessionStorage.getItem("token");
  const token = tokenValue ? `Bearer ${tokenValue}` : "";

  // Initialization
  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
    fetchCities();

    // Auto-refresh campaigns every 5 seconds for live status
    const intervalId = setInterval(() => {
      fetchCampaigns();
    }, 5000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    let logsInterval;
    if (showLogsDialog && selectedCampaign) {
        fetchLogs(selectedCampaign._id);
        logsInterval = setInterval(() => {
            fetchLogs(selectedCampaign._id);
            fetchCampaigns(); // refresh header stats
        }, 5000);
    }
    return () => clearInterval(logsInterval);
  }, [showLogsDialog, selectedCampaign]);

  const fetchLogs = async (campaignId) => {
    try {
        const res = await getData(`whatsapp-campaign/logs/${campaignId}`, null, token, false);
        if (res.success) setCampaignLogs(res.logs);
    } catch (err) { console.error(err); }
  };

  const handleStartCampaign = async (campaignId) => {
    try {
        const res = await putData(`whatsapp-campaign/start/${campaignId}`, {}, token);
        if (res && res.success) {
            fetchCampaigns();
        }
    } catch (err) { console.error(err); }
  };

  const handleResendFailed = async (campaignId) => {
    if (!window.confirm("Are you sure you want to retry all failed messages?")) return;
    try {
        const res = await putData(`whatsapp-campaign/resend-failed/${campaignId}`, {}, token);
        if (res && res.success) {
            fetchCampaigns();
        }
    } catch (err) { console.error(err); }
  };

  const handleDuplicateCampaign = async (campaignId) => {
    if (!window.confirm("Are you sure you want to duplicate this campaign?")) return;
    try {
        const res = await postData(`whatsapp-campaign/duplicate/${campaignId}`, {}, token);
        if (res && res.success) {
            fetchCampaigns();
        }
    } catch (err) { console.error(err); }
  };

  const fetchCampaigns = async () => {
    try {
      const res = await getData("whatsapp-campaign/list", null, token);
      if (res.success) setCampaigns(res.campaigns);
    } catch (err) { console.error(err); }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await getData("whatsapp-campaign/templates", null, token);
      if (res.success) setTemplates(res.templates);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchCities = async () => {
    try {
      const response = await getData("campaign/city/list", null, token);
      setCities(response.cities || []);
    } catch (error) { console.error(error); }
  };

  const fetchCustomers = async (filters) => {
    setLoading(true);
    try {
      const response = await postData("campaign/customer/list", filters, token);
      if (response && response.customers) {
        const uniqueMap = new Map();
        response.customers.forEach(c => {
          if (c.mobile && c.mobile.trim() !== "" && !uniqueMap.has(c.mobile)) {
            uniqueMap.set(c.mobile, c);
          }
        });
        const uniqueCustomers = Array.from(uniqueMap.values());
        setCustomers(uniqueCustomers);
        setSelectedCustomers(uniqueCustomers.map(c => c._id));
      }
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const handleTemplateChange = (e) => {
    const tName = e.target.value;
    const template = templates.find(t => t._id === tName);
    setSelectedTemplate(template);
    setFormData(prev => ({
      ...prev,
      templateName: tName,
      templateLanguage: template?.language || "en_US",
      variableValues: template?.variables?.reduce((acc, curr) => ({ ...acc, [curr]: "" }), {}) || {}
    }));
  };

  const handleVariableChange = (varName, value) => {
    setFormData(prev => ({
      ...prev,
      variableValues: { ...prev.variableValues, [varName]: value }
    }));
  };

  const handleApplyFilters = async () => {
    const { customerType, ...filters } = filterData;
    await fetchCustomers(filters);
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    if (audienceSource === 'database' && !selectedCustomers.length) return alert("Please select customers from the database");
    if (audienceSource === 'excel' && !externalFile) return alert("Please upload an Excel file");
    
    try {
        setLoading(true);
        let res;

        const fd = new FormData();
        fd.append("name", formData.name);
        fd.append("templateName", formData.templateName);
        fd.append("templateLanguage", formData.templateLanguage);
        fd.append("variableValues", JSON.stringify(formData.variableValues));
        if (formData.isScheduled) fd.append("scheduledAt", formData.date);

        if (selectedTemplate && selectedTemplate.headerType !== 'NONE') {
            fd.append("headerMediaType", selectedTemplate.headerType);
            if (formData.headerFile) {
                fd.append("headerMedia", formData.headerFile);
            }
        }

        if (audienceSource === 'excel') {
            fd.append("file", externalFile);
        } else {
            fd.append("customerIds", JSON.stringify(selectedCustomers));
        }
        
        res = await postFileUpload("whatsapp-campaign/create", fd, token);

        if (res && res.success) {
            setShowForm(false);
            fetchCampaigns();
            resetForm();
        }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    const variables = extractVariables(templateFormData.bodyText + " " + templateFormData.headerContent);
    
    try {
        let res;
        if (templateFormData.headerType === 'MEDIA' && templateFormData.headerFile) {
            const formData = new FormData();
            formData.append("name", templateFormData.name);
            formData.append("category", templateFormData.category);
            formData.append("templateName", templateFormData.templateName);
            formData.append("language", templateFormData.language);
            formData.append("headerType", templateFormData.headerType);
            formData.append("bodyText", templateFormData.bodyText);
            formData.append("content", templateFormData.bodyText);
            formData.append("footerText", templateFormData.footerText);
            formData.append("variables", JSON.stringify(variables));
            formData.append("media", templateFormData.headerFile);
            
            res = await postFileUpload("whatsapp-campaign/templates", formData, token);
        } else {
            const payload = {
                ...templateFormData,
                content: templateFormData.bodyText, // Backend compatibility
                variables: variables
            };
            res = await postData("whatsapp-campaign/templates", payload, token);
        }
        
        if (res && res.success) {
            setShowTemplateForm(false);
            fetchTemplates();
            setTemplateFormData({ name: "", category: "MARKETING", templateName: "", language: "en", headerType: "NONE", headerContent: "", headerFile: null, headerPreview: "", bodyText: "", footerText: "", buttons: [] });
        }
    } catch (err) { console.error(err); }
  };

  const resetForm = () => {
    setFormData({ name: "", templateName: "", variableValues: {}, headerFile: null, scheduledAt: null, isScheduled: false, date: "" });
    setSelectedCustomers([]);
    setSelectedTemplate(null);
    setExternalFile(null);
    setVariableTypes({});
    setAudienceSource('database');
  };

  useEffect(() => {
      if (audienceSource === 'database') {
          setDynamicFields(['name', 'mobile', 'email', 'city', 'state', 'gender', 'dob']);
      } else if (!externalFile) {
          setDynamicFields([]);
      }
  }, [audienceSource, externalFile]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setExternalFile(file);
    if (file) {
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const data = evt.target.result;
                const workbook = XLSX.read(data, { type: 'binary' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const headers = [];
                const range = XLSX.utils.decode_range(worksheet['!ref']);
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    const cell = worksheet[XLSX.utils.encode_cell({ c: C, r: range.s.r })];
                    if (cell && cell.v) headers.push(cell.v.toString().trim());
                }
                setDynamicFields(headers);
            } catch (err) {
                console.error("Error parsing Excel:", err);
            }
        };
        reader.readAsBinaryString(file);
    } else {
        setDynamicFields([]);
    }
  };

  const getStatusChip = (status) => {
    const colors = {
        pending: "default",
        scheduled: "info",
        running: "warning",
        completed: "success",
        cancelled: "error",
        sent: "primary",
        delivered: "info",
        read: "success",
        failed: "error"
    };
    return <Chip label={status.toUpperCase()} size="small" color={colors[status] || "default"} sx={{ fontWeight: 800, borderRadius: '6px' }} />;
  };

  if (showForm) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, margin: "0 auto", backgroundColor: '#fafbfd', minHeight: '100vh', borderRadius: '24px' }}>
        <Card sx={{ borderRadius: "24px", boxShadow: "0 10px 40px rgba(0,0,0,0.04)", border: "1px solid #edf2f7" }}>
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Box display="flex" alignItems="center" mb={5}>
              <IconButton onClick={() => setShowForm(false)} sx={{ mr: 2, color: "#1a1f36", backgroundColor: '#f1f5f9', '&:hover': { backgroundColor: '#e2e8f0' } }}><FaArrowLeft size={18} /></IconButton>
              <Typography variant="h4" sx={{ fontFamily: "Mulish, sans-serif", fontWeight: 800, color: '#1a1f36', letterSpacing: '-0.5px' }}>New WhatsApp Campaign</Typography>
            </Box>
            
            <form onSubmit={handleCreateCampaign}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField 
                    fullWidth required label="Campaign Name" 
                    value={formData.name} 
                    onChange={e => setFormData({...formData, name: e.target.value})} 
                  />
                </Grid>


                <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Audience Selection</Typography>
                    
                    <Box sx={{ mb: 3 }}>
                        <RadioGroup row value={audienceSource} onChange={(e) => setAudienceSource(e.target.value)}>
                            <FormControlLabel value="database" control={<Radio sx={{ color: '#25D366', '&.Mui-checked': { color: '#25D366' } }} />} label="Use Database Customers" />
                            <FormControlLabel value="excel" control={<Radio sx={{ color: '#25D366', '&.Mui-checked': { color: '#25D366' } }} />} label="Import from Excel (.xlsx)" />
                        </RadioGroup>
                    </Box>

                    {audienceSource === 'database' ? (
                        <>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth>
                                        <InputLabel>City</InputLabel>
                                        <Select multiple value={selectedCity} onChange={e => { setSelectedCity(e.target.value); setFilterData({...filterData, city: e.target.value})}} renderValue={s => s.join(", ")}>
                                            {cities.map(c => <MenuItem key={c} value={c}><Checkbox checked={selectedCity.includes(c)} /><ListItemText primary={c} /></MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <FormControl fullWidth>
                                        <InputLabel>Gender</InputLabel>
                                        <Select multiple value={filterData.gender} onChange={e => setFilterData({...filterData, gender: e.target.value})} renderValue={s => s.join(", ")}>
                                            <MenuItem value="male"><Checkbox checked={filterData.gender.includes("male")} />Male</MenuItem>
                                            <MenuItem value="female"><Checkbox checked={filterData.gender.includes("female")} />Female</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth>
                                        <InputLabel>Customer Type</InputLabel>
                                        <Select multiple value={filterData.customerType || []} onChange={e => setFilterData({...filterData, customerType: e.target.value})} renderValue={s => s.join(", ")}>
                                            <MenuItem value="customer"><Checkbox checked={(filterData.customerType || []).includes("customer")} />Customer</MenuItem>
                                            <MenuItem value="business"><Checkbox checked={(filterData.customerType || []).includes("business")} />Business</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <Button fullWidth variant="outlined" onClick={handleApplyFilters} sx={{ height: '56px' }} disabled={loading}>
                                        {loading ? <CircularProgress size={24} /> : <>Apply <FaArrowRight style={{marginLeft: 8}} /></>}
                                    </Button>
                                </Grid>
                                <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Selected: <strong>{selectedCustomers.length}</strong> unique customers
                                    </Typography>
                                </Grid>
                            </Grid>

                            {customers.length > 0 && (
                                <Box sx={{ mt: 3 }}>
                                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 250, borderRadius: '8px' }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 700 }}>Name</TableCell>
                                                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 700 }}>Mobile</TableCell>
                                                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 700, width: 80 }}>Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {customers.map((c) => (
                                                    <TableRow key={c._id}>
                                                        <TableCell>{c.name || "N/A"}</TableCell>
                                                        <TableCell>{c.mobile}</TableCell>
                                                        <TableCell>
                                                            <Checkbox 
                                                                size="small"
                                                                checked={selectedCustomers.includes(c._id)} 
                                                                onChange={() => {
                                                                    setSelectedCustomers(prev => 
                                                                        prev.includes(c._id) ? prev.filter(id => id !== c._id) : [...prev, c._id]
                                                                    );
                                                                }} 
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Box sx={{ p: 4, border: '2px dashed #cbd5e1', borderRadius: '12px', backgroundColor: '#f8fafc', textAlign: 'center' }}>
                            <input 
                                type="file" 
                                accept=".xlsx"
                                id="excel-upload"
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                            <label htmlFor="excel-upload">
                                <Button variant="outlined" component="span" startIcon={<FaPlus />} sx={{ borderColor: '#25D366', color: '#25D366', '&:hover': { borderColor: '#1ebe57', backgroundColor: '#f0fdf4' } }}>
                                    {externalFile ? externalFile.name : "Select Excel File"}
                                </Button>
                            </label>
                            <Typography variant="caption" display="block" sx={{ mt: 2, color: '#64748b' }}>
                                Ensure your Excel sheet has exactly two headers: <strong>Name</strong> and <strong>Mobile</strong>.
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <Button 
                                    variant="text" 
                                    startIcon={<FaDownload />} 
                                    href={`${API_URL}whatsapp-campaign/download-template`}
                                    target="_blank"
                                    sx={{ textTransform: 'none', fontWeight: 600, color: '#0ea5e9' }}
                                >
                                    Download Sample Excel
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Select WhatsApp Template</InputLabel>
                    <Select value={formData.templateName} onChange={handleTemplateChange} label="Select WhatsApp Template">
                      {templates.filter(t => t.status === 'APPROVED').map(t => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>

                {selectedTemplate && (
                    <Grid item xs={12}>
                        <Box sx={{ p: 2, backgroundColor: '#f0f2f5', borderRadius: '12px', border: '1px solid #ddd', mb: 3 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>TEMPLATE PREVIEW</Typography>
                            <Typography sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{selectedTemplate.content}</Typography>
                        </Box>
                        
                        {selectedTemplate.variables.length > 0 && (
                            <>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>Template Variables</Typography>
                                <Grid container spacing={2}>
                                    {selectedTemplate.variables.map(v => (
                                        <Grid item xs={12} md={4} key={v}>
                                            <Box sx={{ border: '1px solid #e2e8f0', p: 1.5, borderRadius: '8px', backgroundColor: '#fff' }}>
                                                <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 700, color: '#64748b' }}>
                                                    Variable {`{{${v}}}`}
                                                </Typography>
                                                <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                                                    <InputLabel>Data Source</InputLabel>
                                                    <Select 
                                                        value={variableTypes[v] || 'static'}
                                                        onChange={e => {
                                                            const type = e.target.value;
                                                            setVariableTypes(prev => ({ ...prev, [v]: type }));
                                                            handleVariableChange(v, type === 'dynamic' ? (dynamicFields[0] ? `{{dynamic:${dynamicFields[0]}}}` : "") : "");
                                                        }}
                                                        label="Data Source"
                                                    >
                                                        <MenuItem value="static">Static Text</MenuItem>
                                                        <MenuItem value="dynamic">Dynamic Field</MenuItem>
                                                    </Select>
                                                </FormControl>
                                                {variableTypes[v] === 'dynamic' ? (
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Select Field</InputLabel>
                                                        <Select
                                                            value={formData.variableValues[v]?.replace('{{dynamic:', '')?.replace('}}', '') || ''}
                                                            onChange={e => handleVariableChange(v, `{{dynamic:${e.target.value}}}`)}
                                                            label="Select Field"
                                                        >
                                                            {dynamicFields.map(f => (
                                                                <MenuItem key={f} value={f}>{f}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                ) : (
                                                    <TextField 
                                                        size="small"
                                                        fullWidth label="Static Value" 
                                                        value={formData.variableValues[v] || ""} 
                                                        onChange={e => handleVariableChange(v, e.target.value)}
                                                    />
                                                )}
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </>
                        )}

                        {selectedTemplate.headerType && selectedTemplate.headerType !== 'NONE' && (
                            <Box sx={{ mt: 3, p: 2, border: '1px dashed #cbd5e1', borderRadius: '12px' }}>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>Header Media ({selectedTemplate.headerType})</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>This template requires a media file for its header.</Typography>
                                <Button variant="outlined" component="label" sx={{ textTransform: 'none' }}>
                                    Upload {selectedTemplate.headerType}
                                    <input type="file" hidden onChange={e => setFormData({ ...formData, headerFile: e.target.files[0] })} />
                                </Button>
                                {formData.headerFile && <Typography variant="body2" sx={{ mt: 1, color: '#10b981' }}>Selected: {formData.headerFile.name}</Typography>}
                            </Box>
                        )}
                    </Grid>
                )}

                <Grid item xs={12} md={6}>
                    <FormControlLabel control={<Checkbox checked={formData.isScheduled} onChange={e => setFormData({...formData, isScheduled: e.target.checked})} />} label="Schedule for later" />
                    {formData.isScheduled && (
                        <TextField fullWidth type="datetime-local" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} InputLabelProps={{ shrink: true }} sx={{ mt: 1 }} />
                    )}
                </Grid>

                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                  <Button variant="outlined" onClick={() => setShowForm(false)} sx={{ px: 4, borderRadius: '10px', fontWeight: 700, color: '#64748b', borderColor: '#cbd5e1' }}>Cancel</Button>
                  <Button type="submit" variant="contained" disabled={loading} sx={{ backgroundColor: "#25D366", color: "white", px: 6, py: 1.5, borderRadius: '10px', fontWeight: 800, boxShadow: '0 4px 14px 0 rgba(37, 211, 102, 0.39)', '&:hover': { backgroundColor: '#1ebe57', transform: 'translateY(-1px)', boxShadow: '0 6px 20px rgba(37, 211, 102, 0.23)' }, transition: 'all 0.2s' }}>
                    {loading ? "Creating..." : "Launch Campaign"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, margin: "0 auto", backgroundColor: '#fafbfd', minHeight: '100vh', borderRadius: '24px' }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 5 }}>
        <Box display="flex" alignItems="center" gap={2}>
            <FaWhatsapp size={48} color="#25D366" style={{ filter: "drop-shadow(0px 4px 6px rgba(37,211,102,0.3))" }} />
            <Box>
                <Typography variant="h3" sx={{ fontWeight: 900, fontFamily: "Mulish, sans-serif", letterSpacing: '-0.5px', color: '#1a1f36' }}>WhatsApp Campaigns</Typography>
                <Typography variant="body1" sx={{ color: '#697386', mt: 0.5 }}>Manage your WhatsApp marketing and templates</Typography>
            </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" startIcon={<FaPlus />} onClick={() => setShowTemplateForm(true)} sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, borderColor: '#d3d9e5', color: '#3c4257', '&:hover': { backgroundColor: '#f7fafc', borderColor: '#c1c9d2' }, px: 3, py: 1.5 }}>Manage Templates</Button>
            <Button variant="contained" startIcon={<IoLogoWhatsapp />} onClick={() => setShowForm(true)} sx={{ backgroundColor: '#25D366', color: 'white', borderRadius: '12px', textTransform: 'none', fontWeight: 700, px: 3, py: 1.5, boxShadow: '0 4px 14px 0 rgba(37, 211, 102, 0.39)', '&:hover': { backgroundColor: '#1ebe57', transform: 'translateY(-1px)', boxShadow: '0 6px 20px rgba(37, 211, 102, 0.23)' }, transition: 'all 0.2s ease-in-out' }}>New Campaign</Button>
        </Box>
      </Box>

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 4, '& .MuiTabs-indicator': { backgroundColor: '#25D366', height: '3px', borderRadius: '3px' } }}>
        <Tab label="Campaigns" sx={{ textTransform: 'none', fontWeight: 700, fontSize: '1.05rem', color: '#697386', '&.Mui-selected': { color: '#1a1f36' } }} />
        <Tab label="Templates" sx={{ textTransform: 'none', fontWeight: 700, fontSize: '1.05rem', color: '#697386', '&.Mui-selected': { color: '#1a1f36' } }} />
      </Tabs>

      {activeTab === 0 ? (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "16px", border: "1px solid #e3e8ee", boxShadow: "0 4px 20px rgba(0,0,0,0.03)", overflow: 'hidden' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { backgroundColor: '#f7fafc', fontWeight: 800, fontFamily: 'Mulish', color: '#4f566b', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', py: 2.5 } }}>
                <TableCell>Campaign ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Audience</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sent / Failed</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map(c => (
                <TableRow key={c._id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 }, transition: 'background-color 0.2s', cursor: 'pointer' }}>
                  <TableCell sx={{ color: '#1a1f36', fontWeight: 600 }}>{c.campId}</TableCell>
                  <TableCell sx={{ color: '#3c4257', fontWeight: 500 }}>{c.name}</TableCell>
                  <TableCell>
                      <Chip label={c.templateName || "N/A"} size="small" sx={{ backgroundColor: '#f3f4f6', color: '#4b5563', fontWeight: 600, borderRadius: '6px' }} />
                  </TableCell>
                  <TableCell sx={{ color: '#4f566b' }}>{c.stats.total} Customers</TableCell>
                  <TableCell>{getStatusChip(c.status)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: '#10b981', fontWeight: 700, fontSize: '0.9rem' }}>{c.stats.sent}</Typography>
                        <Typography sx={{ color: '#9ca3af' }}>/</Typography>
                        <Typography sx={{ color: '#ef4444', fontWeight: 700, fontSize: '0.9rem' }}>{c.stats.failed}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: '#6b7280', fontSize: '0.9rem' }}>{new Date(c.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); setSelectedCampaign(c); setShowLogsDialog(true); }} sx={{ textTransform: 'none' }}>History</Button>
                        {(c.status === 'scheduled' || c.status === 'cancelled' || c.status === 'pending') && (
                            <Button size="small" variant="contained" color="primary" onClick={(e) => { e.stopPropagation(); handleStartCampaign(c._id); }} sx={{ textTransform: 'none' }}>Start</Button>
                        )}
                        {c.stats.failed > 0 && c.status !== 'running' && c.status !== 'pending' && (
                            <Button size="small" variant="outlined" color="error" onClick={(e) => { e.stopPropagation(); handleResendFailed(c._id); }} sx={{ textTransform: 'none' }}>Retry Failed</Button>
                        )}
                        <Button size="small" variant="outlined" color="secondary" onClick={(e) => { e.stopPropagation(); handleDuplicateCampaign(c._id); }} sx={{ textTransform: 'none' }}>Duplicate</Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {campaigns.length === 0 && <TableRow><TableCell colSpan={7} align="center">No campaigns found</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3, gap: 2 }}>
                <Button variant="outlined" startIcon={<FaSync />} onClick={fetchTemplates} disabled={loading} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, color: '#25D366', borderColor: '#25D366', '&:hover': { backgroundColor: '#e8f9ed', borderColor: '#1ebe57' }, px: 3 }}>
                    {loading ? "Syncing..." : "Sync from Meta"}
                </Button>
                <Button variant="contained" startIcon={<FaPlus />} onClick={() => setShowTemplateForm(true)} sx={{ backgroundColor: '#1a1f36', color: 'white', borderRadius: '8px', textTransform: 'none', fontWeight: 700, px: 3, boxShadow: '0 4px 14px 0 rgba(26, 31, 54, 0.2)', '&:hover': { backgroundColor: '#0f1322' } }}>
                    Add New Template
                </Button>
            </Box>
            
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "16px", border: "1px solid #e3e8ee", boxShadow: "0 4px 20px rgba(0,0,0,0.03)", overflow: 'hidden' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { backgroundColor: '#f7fafc', fontWeight: 800, fontFamily: 'Mulish', color: '#4f566b', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', py: 2.5 } }}>
                    <TableCell>Template Name</TableCell>
                    <TableCell>Language</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Variables</TableCell>
                    <TableCell sx={{ width: '40%' }}>Preview Snippet</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templates.map(t => (
                    <TableRow key={t._id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 }, transition: 'background-color 0.2s' }}>
                        <TableCell sx={{ color: '#1a1f36', fontWeight: 700 }}>{t.name}</TableCell>
                        <TableCell sx={{ color: '#4f566b' }}>{t.language}</TableCell>
                        <TableCell>
                            <Chip label={t.status} size="small" sx={{ backgroundColor: t.status === 'APPROVED' ? '#e6f4ea' : (t.status === 'REJECTED' ? '#fce8e6' : '#fff3e0'), color: t.status === 'APPROVED' ? '#137333' : (t.status === 'REJECTED' ? '#c5221f' : '#b06000'), fontWeight: 700, fontSize: '0.7rem', borderRadius: '6px' }} />
                        </TableCell>
                        <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {t.variables && t.variables.length > 0 ? t.variables.map(v => <Chip key={v} label={`{{${v}}}`} size="small" sx={{ fontSize: '0.7rem', backgroundColor: '#f1f5f9', color: '#64748b', fontWeight: 600, borderRadius: '4px' }} />) : <Typography variant="caption" sx={{ color: '#9ca3af' }}>None</Typography>}
                            </Box>
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2" sx={{ color: '#475569', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {t.content || <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>No preview available</span>}
                            </Typography>
                        </TableCell>
                    </TableRow>
                  ))}
                  {templates.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: '#64748b' }}>No templates found. Try syncing from Meta.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
        </Box>
      )}

      {/* Add Template Dialog */}
      <Dialog open={showTemplateForm} onClose={() => setShowTemplateForm(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '16px', boxShadow: '0 24px 48px rgba(0,0,0,0.15)' } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1.5rem', p: 3, borderBottom: '1px solid #f1f5f9', color: '#1a1f36' }}>
            Register Meta WhatsApp Template
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
            <Grid container sx={{ minHeight: '650px' }}>
                <Grid item xs={12} md={7} sx={{ p: 4, borderRight: '1px solid #f1f5f9', overflowY: 'auto', maxHeight: '75vh' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Configure your template matching Meta's requirements.</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Category</InputLabel>
                                <Select value={templateFormData.category} label="Category" onChange={e => setTemplateFormData({...templateFormData, category: e.target.value})}>
                                    <MenuItem value="MARKETING">Marketing</MenuItem>
                                    <MenuItem value="UTILITY">Utility</MenuItem>
                                    <MenuItem value="AUTHENTICATION">Authentication</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Language</InputLabel>
                                <Select value={templateFormData.language} label="Language" onChange={e => setTemplateFormData({...templateFormData, language: e.target.value})}>
                                    <MenuItem value="en">English (en)</MenuItem>
                                    <MenuItem value="en_US">English (US)</MenuItem>
                                    <MenuItem value="es">Spanish (es)</MenuItem>
                                    <MenuItem value="hi">Hindi (hi)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth label="Internal Name" value={templateFormData.name} onChange={e => setTemplateFormData({...templateFormData, name: e.target.value})} placeholder="e.g. Order Confirmation" />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth label="Meta Template Name" value={templateFormData.templateName} onChange={e => setTemplateFormData({...templateFormData, templateName: e.target.value})} placeholder="lowercase_alphanumeric" />
                        </Grid>
                        
                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography sx={{ fontWeight: 700, mb: 2 }}>Header (Optional)</Typography>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Header Type</InputLabel>
                                <Select value={templateFormData.headerType} label="Header Type" onChange={e => setTemplateFormData({...templateFormData, headerType: e.target.value, headerContent: '', headerFile: null, headerPreview: ''})}>
                                    <MenuItem value="NONE">None</MenuItem>
                                    <MenuItem value="TEXT">Text</MenuItem>
                                    <MenuItem value="MEDIA">Media (Image/Video/Document)</MenuItem>
                                </Select>
                            </FormControl>
                            {templateFormData.headerType === 'TEXT' && (
                                <TextField fullWidth label="Header Text" value={templateFormData.headerContent} onChange={e => setTemplateFormData({...templateFormData, headerContent: e.target.value})} placeholder="Header with {{1}}" />
                            )}
                            {templateFormData.headerType === 'MEDIA' && (
                                <Box>
                                    <Button variant="outlined" component="label" fullWidth sx={{ height: '56px', borderColor: '#c4c4c4', color: '#666', justifyContent: 'flex-start', px: 2 }}>
                                        {templateFormData.headerFile ? templateFormData.headerFile.name : "Upload Media File"}
                                        <input type="file" hidden accept="image/*,video/*,application/pdf" onChange={(e) => {
                                            if (e.target.files[0]) {
                                                const file = e.target.files[0];
                                                setTemplateFormData({
                                                    ...templateFormData, 
                                                    headerFile: file, 
                                                    headerPreview: URL.createObjectURL(file)
                                                });
                                            }
                                        }} />
                                    </Button>
                                </Box>
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography sx={{ fontWeight: 700, mb: 2 }}>Body (Required)</Typography>
                            <TextField fullWidth multiline rows={4} label="Body Text" value={templateFormData.bodyText} onChange={e => setTemplateFormData({...templateFormData, bodyText: e.target.value})} placeholder="Hello {{1}}, your order {{2}} is confirmed." />
                            <Typography variant="caption" color="text.secondary">Use {'{{1}}'}, {'{{2}}'} for variables. They will be auto-extracted.</Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography sx={{ fontWeight: 700, mb: 2 }}>Footer (Optional)</Typography>
                            <TextField fullWidth label="Footer Text" value={templateFormData.footerText} onChange={e => setTemplateFormData({...templateFormData, footerText: e.target.value})} placeholder="Footer text" />
                        </Grid>
                    </Grid>
                </Grid>
                
                {/* PREVIEW PANE */}
                <Grid item xs={12} md={5} sx={{ backgroundColor: '#e5ddd5', backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")', backgroundSize: 'cover', p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="overline" sx={{ backgroundColor: 'rgba(255,255,255,0.7)', px: 2, py: 0.5, borderRadius: '12px', mb: 3 }}>Preview</Typography>
                    
                    <Box sx={{ width: '100%', maxWidth: '340px', backgroundColor: '#dcf8c6', borderRadius: '12px 12px 12px 0', p: 1.5, pb: 3, boxShadow: '0 2px 4px rgba(0,0,0,0.1)', position: 'relative', wordBreak: 'break-word' }}>
                        {templateFormData.headerType === 'MEDIA' && templateFormData.headerPreview && (
                            <Box sx={{ width: '100%', height: '160px', backgroundColor: '#f0f0f0', borderRadius: '8px', mb: 1, backgroundImage: `url(${templateFormData.headerPreview})`, backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            </Box>
                        )}
                        {templateFormData.headerType === 'TEXT' && templateFormData.headerContent && (
                            <Typography sx={{ fontWeight: 700, fontSize: '15px', mb: 1, color: '#111' }}>
                                {templateFormData.headerContent.replace(/\{\{(\d+)\}\}/g, '[$1]')}
                            </Typography>
                        )}
                        
                        <Typography sx={{ fontSize: '14.5px', whiteSpace: 'pre-wrap', color: '#111', lineHeight: 1.4 }}>
                            {templateFormData.bodyText ? templateFormData.bodyText.replace(/\{\{(\d+)\}\}/g, '[$1]') : <span style={{ color: '#888', fontStyle: 'italic' }}>Your message body will appear here...</span>}
                        </Typography>
                        
                        {templateFormData.footerText && (
                            <Typography sx={{ fontSize: '12.5px', color: 'rgba(0,0,0,0.5)', mt: 1 }}>
                                {templateFormData.footerText}
                            </Typography>
                        )}

                        {/* Fake WhatsApp Time */}
                        <Typography sx={{ fontSize: '11px', color: 'rgba(0,0,0,0.45)', position: 'absolute', bottom: '6px', right: '12px' }}>10:42 AM</Typography>
                    </Box>
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #f1f5f9', backgroundColor: '#f8fafc' }}>
            <Button onClick={() => setShowTemplateForm(false)} sx={{ fontWeight: 700, color: '#64748b', '&:hover': { backgroundColor: '#f1f5f9' }, px: 3 }}>Cancel</Button>
            <Button variant="contained" onClick={handleCreateTemplate} disabled={!templateFormData.name || !templateFormData.templateName || !templateFormData.bodyText} sx={{ backgroundColor: '#25D366', color: 'white', px: 5, py: 1.2, borderRadius: '8px', fontWeight: 700, boxShadow: '0 4px 14px 0 rgba(37, 211, 102, 0.39)', '&:hover': { backgroundColor: '#1ebe57' } }}>Save Template</Button>
        </DialogActions>
      </Dialog>

      {/* Campaign Logs Dialog */}
      <Dialog open={showLogsDialog} onClose={() => { setShowLogsDialog(false); setSelectedCampaign(null); }} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px', boxShadow: '0 24px 48px rgba(0,0,0,0.15)' } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a1f36', borderBottom: '1px solid #f1f5f9' }}>
            Campaign History: {selectedCampaign?.name}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
            {selectedCampaign && (
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#f1f5f9', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: '#334155' }}>Initiation Details</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">Campaign ID</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCampaign.campId}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">Template Used</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCampaign.templateName}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">Status</Typography>
                            <Box sx={{ mt: 0.5 }}>{getStatusChip(selectedCampaign.status)}</Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">Created At</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{new Date(selectedCampaign.createdAt).toLocaleString()}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">Scheduled For</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCampaign.scheduledAt ? new Date(selectedCampaign.scheduledAt).toLocaleString() : 'Immediate'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">Variables</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={selectedCampaign.variableValues && Object.keys(selectedCampaign.variableValues).length > 0 ? Object.entries(selectedCampaign.variableValues).map(([k, v]) => `${k}: ${v}`).join(', ') : 'None'}>
                                {selectedCampaign.variableValues && Object.keys(selectedCampaign.variableValues).length > 0
                                    ? Object.entries(selectedCampaign.variableValues).map(([k, v]) => `${k}: ${v}`).join(', ')
                                    : 'None'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
            )}
            <Box sx={{ mb: 3, display: 'flex', gap: 3, backgroundColor: '#f8fafc', p: 2, borderRadius: '8px' }}>
                <Typography sx={{ fontWeight: 600 }}>Total: <strong>{campaigns.find(c => c._id === selectedCampaign?._id)?.stats?.total || 0}</strong></Typography>
                <Typography sx={{ fontWeight: 600, color: '#10b981' }}>Sent: <strong>{campaigns.find(c => c._id === selectedCampaign?._id)?.stats?.sent || 0}</strong></Typography>
                <Typography sx={{ fontWeight: 600, color: '#ef4444' }}>Failed: <strong>{campaigns.find(c => c._id === selectedCampaign?._id)?.stats?.failed || 0}</strong></Typography>
                <Typography sx={{ fontWeight: 600, color: '#f59e0b' }}>Pending: <strong>{campaigns.find(c => c._id === selectedCampaign?._id)?.stats?.pending || 0}</strong></Typography>
            </Box>
            <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e3e8ee", maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, backgroundColor: '#f1f5f9' }}>Customer</TableCell>
                            <TableCell sx={{ fontWeight: 700, backgroundColor: '#f1f5f9' }}>Mobile</TableCell>
                            <TableCell sx={{ fontWeight: 700, backgroundColor: '#f1f5f9' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700, backgroundColor: '#f1f5f9' }}>Sent At</TableCell>
                            <TableCell sx={{ fontWeight: 700, backgroundColor: '#f1f5f9' }}>Error</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {campaignLogs.map(log => (
                            <TableRow key={log._id} hover>
                                <TableCell>{log.customerId ? log.customerId.name : log.externalName || "N/A"}</TableCell>
                                <TableCell>{log.mobile}</TableCell>
                                <TableCell>{getStatusChip(log.status)}</TableCell>
                                <TableCell sx={{ color: '#64748b' }}>{log.sentAt ? new Date(log.sentAt).toLocaleString() : "-"}</TableCell>
                                <TableCell sx={{ color: '#ef4444', fontSize: '0.8rem', maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={log.error}>{log.error || "-"}</TableCell>
                            </TableRow>
                        ))}
                        {campaignLogs.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: '#64748b' }}>No logs available for this campaign.</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #f1f5f9' }}>
            <Button onClick={() => { setShowLogsDialog(false); setSelectedCampaign(null); }} sx={{ fontWeight: 700, color: '#64748b' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Whatsapp;
